import React, { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { useLocale } from '../../context/LocaleContext';
import { getSelectedEnvironment } from '../../utils/apiEnvironment';
import { getSpawnPoints, requestSpawnPoints } from '../../api/spawnPoints';
import SpawnWorkspace from './SpawnWorkspace';
import { SPAWN_COPY } from './spawnCopy';
let sessionSequence = 0;
export function newestCatalog(previous, next) {
  if (!next) return previous;
  return previous && previous.revision > next.revision ? previous : next;
}
export function SpawnTarget({ environment, token, userId, role, copy }) {
  const client = useQueryClient();
  // A non-secret session identity keeps old requests isolated even when the
  // operator returns to the same environment/user after leaving this screen.
  const session = useMemo(() => ({ id: ++sessionSequence, active: !!token, environment, userId, role }), [environment, token, userId, role]);
  const key = useMemo(() => ['spawnPoints', environment, userId, session.id], [environment, userId, session]);
  useEffect(() => {
    session.active = true;
    return () => { session.active = false; client.cancelQueries({ queryKey: key, exact: true }); };
  }, [client, key, session]);
  function newestKnownCatalog(incoming) {
    return client.getQueriesData({ queryKey: ['spawnPoints', environment, userId] }).reduce((latest, [, data]) => newestCatalog(latest, data), incoming);
  }
  function assertCurrent(signal) {
    if (!session.active || signal?.aborted || getSelectedEnvironment() !== environment) throw Object.assign(new Error('Obsolete spawn request'), { uncertain: true });
  }
  const query = useQuery({
    queryKey: key,
    queryFn: async ({ signal }) => { assertCurrent(signal); const result = await getSpawnPoints(token, environment, signal); assertCurrent(signal); return newestKnownCatalog(result); },
    enabled: !!token,
    retry: false,
    // Covers every query completion, including a GET started before a mutation.
    structuralSharing: newestCatalog,
  });
  const writesEnabled = ['test', 'prod'].includes(environment) && role === 'admin' && !!token;
  async function action(operation) {
    assertCurrent();
    if (!writesEnabled) throw Object.assign(new Error(copy.forbidden), { status: 403 });
    const method = { create: 'POST', update: 'PUT', delete: 'DELETE' }[operation.kind];
    const result = await requestSpawnPoints(method, { id: operation.kind === 'create' ? undefined : operation.point.place_id, point: operation.kind === 'delete' ? undefined : operation.point, revision: operation.revision, requestId: operation.requestId }, token, environment);
    assertCurrent();
    client.setQueryData(key, previous => newestCatalog(previous, newestKnownCatalog(result)));
  }
  return <SpawnWorkspace collection={query.data} loading={query.isFetching} queryError={query.error} environment={environment.toUpperCase()} writesEnabled={writesEnabled && !query.isError} copy={copy} onRefresh={() => query.refetch({ throwOnError: true })} onAction={action} />;
}
export default function SpawnPoints() {
  const { token, user, role } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const { locale } = useLocale();
  return <SpawnTarget key={`${apiEnvironment}:${user?.id ?? user?.user_id}:${role}`} environment={apiEnvironment} token={token} userId={user?.id ?? user?.user_id} role={role} copy={SPAWN_COPY[locale] || SPAWN_COPY.ko} />;
}
